import { throttle } from 'es-toolkit';
import { useCallback, useRef } from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { VideoPlayerEvent, video } from '@tracking';

interface UseVideoPlaybackTrackingProps {
  src: string;
  videoRef: React.RefObject<HTMLVideoElement>;
}

const COMPLETION_PERCENTAGE = 100;

function calculatePlayedPercentage(videoRef: HTMLVideoElement) {
  return Math.ceil((videoRef?.currentTime / videoRef?.duration) * 100);
}

const useVideoPlaybackTracking = ({ videoRef, src }: UseVideoPlaybackTrackingProps) => {
  const { trackEvent } = useEventTracking();
  const percentagesTracked = useRef<Set<number>>(new Set());

  const handleVideoPlay = useCallback(() => {
    trackEvent(
      video({
        videoUrl: src,
        playerEvent: VideoPlayerEvent.Started,
        playedPercentage: calculatePlayedPercentage(videoRef.current),
      })
    );
  }, [src, trackEvent, videoRef]);

  const handleVideoPause = useCallback(() => {
    const playedPercentage = calculatePlayedPercentage(videoRef.current);

    if (playedPercentage === COMPLETION_PERCENTAGE) return;

    trackEvent(
      video({
        videoUrl: src,
        playerEvent: VideoPlayerEvent.Paused,
        playedPercentage,
      })
    );
  }, [src, trackEvent, videoRef]);

  const handleVideoEnd = useCallback(() => {
    trackEvent(
      video({
        videoUrl: src,
        playerEvent: VideoPlayerEvent.Completed,
        playedPercentage: COMPLETION_PERCENTAGE,
      })
    );
  }, [src, trackEvent]);

  const handleVideoTimeUpdate = useCallback(() => {
    const playedPercentage = calculatePlayedPercentage(videoRef.current);

    if (playedPercentage % 25 === 0 && !percentagesTracked.current.has(playedPercentage)) {
      percentagesTracked.current.add(playedPercentage);
      trackEvent(
        video({
          videoUrl: src,
          playerEvent: VideoPlayerEvent.Progress,
          playedPercentage,
        })
      );
    }
  }, [src, trackEvent, videoRef]);

  const throttledHandleVideoTimeUpdate = throttle(handleVideoTimeUpdate, 750);

  return {
    handleVideoEnd,
    handleVideoPause,
    handleVideoPlay,
    throttledHandleVideoTimeUpdate,
  };
};

export default useVideoPlaybackTracking;
